import clsx from 'clsx';
import TablePageLayout from '../../../components/tablePageLayout/TablePageLayout';
import styles from './MonitoringFormsSection.module.scss';
import { FC, memo, useMemo } from 'react';
import { ExpandedRowHeader } from '../../../components/expandedRow/header/expandedRowHeader.component';
import { DateFormats, localFormat } from '../../../helpers/date-helpers';
import { AppButton } from 'uikit/buttons';
import { ReactComponent as Edit } from 'src/assets/img/records/edit_24.svg';
import { ReactComponent as Add } from 'src/assets/img/records/add_24.svg';
import { ReactComponent as Eye } from 'src/assets/img/common/eye_24.svg';
import { ReactComponent as PatientIcon } from 'src/assets/img/formBuilder/statusIcons/patient_filled_16.svg';
import { useTranslation } from 'react-i18next';
import { StudyRoleClaimValue } from '../../../services/api/api-client';
import { TypographyStyles } from 'styles';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { useMonitoringFormFillingModal } from './useMonitoringFormFillingModal';
import { useMonitoringFormViewModal } from './useMonitoringFormViewModal';
import { DropDownMenu } from 'uikit/dropDownMenu/dropDownMenu.component';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';
import { useGetMonitoringResultsForStudy } from '../hooks/useGetMonitoringResultsForStudy';
import { first } from 'lodash';

export const ConfigurableMonitoringFormsSection: FC = memo(() => {
  const { t } = useTranslation();
  const { hasStudyClaim } = useHasStudyClaims();
  const { closingApprovedAt, monitoringFormConfigs } = useStudy() ?? {};

  const { isLoading, monitoringFormsResults } = useGetMonitoringResultsForStudy();

  const formFillingModal = useMonitoringFormFillingModal();
  const formViewModal = useMonitoringFormViewModal();

  const itemVms = useMemo(
    () =>
      monitoringFormsResults.map(
        (result) =>
          ({
            formType: result.formConfigTitle ?? t('Monitoring.Page.MonitoringFormsSection.DefaultItemTitle'),
            index: result.formResultId,
            filledAt: result.createdAt,
            patientRange: undefined,
            onEdit: () => formFillingModal.edit(result.formResultId, result.formConfigId),
            onView: () => formViewModal.openHandler(result.formResultId),
          } satisfies FormListItemVm),
      ) ?? [],
    [formFillingModal, formViewModal, monitoringFormsResults, t],
  );

  const addButtonOptions = useMemo(
    () =>
      monitoringFormConfigs?.map(
        (config) =>
          ({
            key: config.configId,
            text: config.title,
            action: () => formFillingModal.fill(config.configId),
          } satisfies DropDownMenuOption),
      ) ?? [],
    [formFillingModal, monitoringFormConfigs],
  );

  return (
    <TablePageLayout.RightBlock loading={isLoading}>
      <ExpandedRowHeader
        text={t('Monitoring.Page.MonitoringFormsSection.Title')}
        rightHeader={
          closingApprovedAt ||
          !hasStudyClaim(StudyRoleClaimValue.MonitoringReportCreate) ||
          !addButtonOptions.length ? undefined : addButtonOptions.length === 1 ? (
            <AppButton
              Icon={Add}
              iconClassName={styles.addButton}
              colorSchema={'primary'}
              variant={'icon-link'}
              onClick={() => formFillingModal.fill(first(addButtonOptions)!.key)}
            />
          ) : (
            <DropDownMenu
              options={addButtonOptions}
              Icon={Add}
              iconClassName={styles.addButton}
              colorSchema={'primary'}
            />
          )
        }
      />
      <div className={styles.formsList}>
        {itemVms.map((vm) => (
          <FormsListItem key={vm.index} vm={vm} />
        ))}
      </div>
      {formFillingModal.modal}
      {formViewModal.modal}
    </TablePageLayout.RightBlock>
  );
});

type FormListItemVm = {
  formType: string;
  index: number;
  filledAt: Date;
  patientRange: string | undefined;
  onEdit: () => void;
  onView: () => void;
};

const FormsListItem: FC<{ vm: FormListItemVm }> = memo(({ vm }) => {
  const { formType, index, filledAt, patientRange, onEdit, onView } = vm;
  const { hasStudyClaim } = useHasStudyClaims();

  return (
    <div className={styles.formsListItem}>
      <div className={styles.row}>
        <div className={clsx(styles.rowLabel, TypographyStyles.heading2)}>
          <span className={styles.formTitle}>{formType}</span>
          <span className={styles.formIndex}>#{index}</span>
        </div>
        <span className={clsx(TypographyStyles.plainText14, styles.date)}>
          {localFormat(filledAt, DateFormats.longDate)}
        </span>
        {hasStudyClaim(StudyRoleClaimValue.MonitoringReportCreate) && (
          <AppButton variant={'icon-link'} colorSchema={'primary'} Icon={Edit} onClick={onEdit} />
        )}
        <AppButton variant={'icon-link'} colorSchema={'primary'} Icon={Eye} onClick={onView} />
      </div>
      {patientRange && (
        <div className={styles.patientsRange}>
          <PatientIcon className={styles.icon} />
          <div className={styles.value}>{patientRange}</div>
        </div>
      )}
    </div>
  );
});
